// AuthCallback — Iter 41. Handles the return trip from Emergent Google
// OAuth. The URL fragment carries `#session_id=<id>`. We synchronously
// grab it during first render (via useLocation().hash), exchange it
// against POST /api/auth/emergent-session, then hand the resulting user
// off to AuthProvider and navigate away. The App-level router detects
// the hash *before* rendering any Route so this screen catches the
// callback even on routes that would otherwise require auth.
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, setToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { t } from "../lib/i18n";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  // Use a ref (not state) so React 18 StrictMode's double-invoke does
  // not attempt to exchange the session_id twice — the second call
  // would 401 because the id is one-time.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      toast.error(t.googleAuthMissing || "Missing session_id in callback URL");
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/emergent-session", {
          session_id: sessionId,
        });
        if (data.token) setToken(data.token);
        setUser({
          email: data.email,
          role: data.role,
          user_id: data.user_id,
          player_id: data.player_id,
          name: data.name,
          picture: data.picture,
        });
        // Clear the hash so a refresh doesn't re-trigger this screen
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        toast.success(t.googleAuthSuccess || `Signed in as ${data.email}`);
        navigate("/", { replace: true });
      } catch (err) {
        const msg = formatApiError(err.response?.data?.detail) || t.googleAuthFailed || "Google sign-in failed";
        toast.error(msg);
        navigate("/login", { replace: true });
      }
    })();
  }, [location.hash, navigate, setUser]);

  return (
    <div
      data-testid="auth-callback"
      className="min-h-[60vh] flex items-center justify-center"
    >
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#00E5FF] font-bold">
          // {t.googleAuthTitle || "Finishing sign-in"}
        </div>
        <div className="font-display font-black uppercase italic text-4xl mt-3">
          {t.pleaseWait || "One moment..."}
        </div>
        <div className="mt-6 h-1 w-48 mx-auto overflow-hidden bg-white/10">
          <div className="h-full w-1/3 bg-[#00E5FF] animate-pulse" />
        </div>
      </div>
    </div>
  );
}
